import { useState, useEffect } from 'react';
import { advisorApiService } from '../services/advisor-api';
import type { StudentFeedbackRequest, StudentInfo } from '../types';
import '../styles/FeedbackForm.css';
import { useAuth } from '../contexts/AuthContext';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  studentInfo: StudentInfo | null;
}

const getAdvisingEmail = (studentType: string, userRole?: string): string => {
  if (userRole !== 'student_user') {
    return 'wh2473@columbia.edu';
  }
  const type = studentType.toLowerCase();
  if (type === 'undergraduate') return 'ug-advising@cs.columbia.edu';
  if (type === 'ms' || type === 'bridge') return 'ms-advising@cs.columbia.edu';
  return 'phd-advising@cs.columbia.edu';
};

export const FeedbackForm = ({ isOpen, onClose, studentInfo }: FeedbackFormProps) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<StudentFeedbackRequest>({
    name: '',
    uni: '',
    email: '',
    feedback: '',
    advising_email: '',
    phone_number: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && studentInfo) {
      const advisingEmail = getAdvisingEmail(studentInfo.student_type, user?.role);
      setFormData((prev) => ({ ...prev, advising_email: advisingEmail }));
    }
  }, [isOpen, studentInfo, user?.role]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.uni.trim()) {
      setError('UNI is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!validateEmail(formData.advising_email)) {
      setError('Advising email is invalid');
      return;
    }
    if (!formData.feedback.trim()) {
      setError('Feedback is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const payload: StudentFeedbackRequest = {
        name: formData.name.trim(),
        uni: formData.uni.trim(),
        email: formData.email.trim(),
        advising_email: formData.advising_email.trim(),
        feedback: formData.feedback.trim(),
      };
      if (formData.phone_number?.trim()) {
        payload.phone_number = formData.phone_number.trim();
      }

      const response = await advisorApiService.submitFeedback(payload);
      if (response.error) {
        setError(response.error);
        setIsSubmitting(false);
        return;
      }
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setIsSubmitting(false);
        onClose();
      }, 3000);
    } catch {
      setError('Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        uni: '',
        email: '',
        feedback: '',
        advising_email: '',
        phone_number: '',
      });
      setError(null);
      setShowSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={handleClose}>
      <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
        {showSuccess ? (
          <div className="feedback-success">
            <h2>Thank You!</h2>
            <p>
              Your feedback has been submitted successfully. Our advisors will review your request
              and reply to the email for follow up.
            </p>
          </div>
        ) : (
          <>
            <div className="feedback-modal-header">
              <h2>Do you seek for more help?</h2>
              <button className="feedback-modal-close" onClick={handleClose} disabled={isSubmitting}>
                ×
              </button>
            </div>
            <form className="feedback-form" onSubmit={handleSubmit}>
              <div className="feedback-form-group">
                <label htmlFor="name">Name <span className="required">*</span></label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  maxLength={255}
                  placeholder="Enter your name"
                />
              </div>
              <div className="feedback-form-group">
                <label htmlFor="uni">UNI <span className="required">*</span></label>
                <input
                  type="text"
                  id="uni"
                  name="uni"
                  value={formData.uni}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  maxLength={255}
                  placeholder="Enter your UNI"
                />
              </div>
              <div className="feedback-form-group">
                <label htmlFor="email">Email <span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  maxLength={255}
                  placeholder="your.email@columbia.edu"
                />
              </div>
              <div className="feedback-form-group">
                <label htmlFor="phone_number">Phone Number (Optional)</label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  maxLength={50}
                  placeholder="(123) 456-7890"
                />
              </div>
              <div className="feedback-form-group">
                <label htmlFor="feedback">Feedback <span className="required">*</span></label>
                <textarea
                  id="feedback"
                  name="feedback"
                  value={formData.feedback}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  maxLength={5000}
                  rows={6}
                  placeholder="Please describe what additional help you need..."
                />
              </div>
              {error && <div className="feedback-error">{error}</div>}
              <div className="feedback-form-actions">
                <button
                  type="button"
                  className="feedback-button-secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="feedback-button-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
